import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import Loader2 from '../../components/loader2';
import showToast from '../../components/ShowToast';
import { jwtDecode } from 'jwt-decode';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import { getBusinessProfileDataWithBID } from "../../components/HomeMessage/Services/getBusinessProfileDataWithBID";
import BusinessProfilePureCSS from '../../profiles/BusinessProfilePureCSS';
import api from '../../api/api.js';


import { 
    Plus, Edit, Trash2, Search, CheckCircle, Clock, AlertCircle, ArrowLeft, 
    GripVertical, LayoutTemplate, Rows, ClipboardList, 
    ChevronRight, Send, UserPlus, FileEdit, Megaphone, MessageSquare, Settings, Star,
    X, DollarSignIcon, CalendarDays, Info
} from 'lucide-react';

// Import Modals
import PhaseModal from '../../components/Projects/Modals/PhaseModal';
import TaskModal from '../../components/Projects/Modals/TaskModal';
import FindContractorsModal from '../../components/Projects/Modals/FindContractorsModal';
import BrowseProsModal from '../../components/Projects/Modals/BrowseProsModal';

import './_AIProjectManager.css';
import styles from '../../components/HomeMessage/Components/MessageSection/RightSidebar/RightSidebar.module.css';

// ==================================================================================
// --- HELPER FUNCTION: Get User Info from Local Storage ---
// ==================================================================================
const getLocalStorageUser = () => {
    // 1. Get raw user type
    const rawUserType = localStorage.getItem('userType') || 'HomeUser';
    
    let userId = 0;
    let apiUserType = "HomeUser";

    // 2. Determine ID based on type (Using your specific logic)
    if (rawUserType === "Home" || rawUserType === "HomeUser") {
        apiUserType = "HomeUser";
        // Parse the string "2" to integer 2
        userId = parseInt(localStorage.getItem('loggedInHomeUserId') || '0', 10);
    } 
    else {
        apiUserType = "BusinessUser"; // Assuming API expects "BusinessUser"
        userId = parseInt(localStorage.getItem('loggedInBusinessId') || '0', 10);
    }

    return { userId, apiUserType };
};

// ==================================================================================
// --- HELPER COMPONENTS ---
// ==================================================================================

const JobStatusWidget = ({ task, onShowDetails }) => {
    const [inviteCount, setInviteCount] = useState(0);
    const [quoteCount, setQuoteCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const jobId = task?.jobId; 

    useEffect(() => {
        if (!jobId) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                const { userId, apiUserType } = getLocalStorageUser();

                if (!userId) {
                    return;
                }

                // 1. Define the Quotes Request specifically to catch 404s
                const quotesRequest = axios.get(`${API_BASE_URL}/api/Jobs/quote/${jobId}`)
                    .catch(err => {
                        // If it's a 404, it just means "No quotes found", which is valid.
                        if (err.response && err.response.status === 404) {
                            return { data: [] }; // Return empty array
                        }
                        // If it's another error (500, Network), allow it to be logged but don't crash
                        console.warn("Error fetching quotes:", err);
                        return { data: [] };
                    });

                // 2. Run requests in parallel
                const [leadsRes, quotesRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/Jobs/getjobleads?userId=${userId}&userType=${apiUserType}`),
                    quotesRequest // Use the safe request defined above
                ]);

                // 3. Process Leads
                const rawLeads = Array.isArray(leadsRes.data) ? leadsRes.data : (leadsRes.data?.result || []);
                const taskLeads = rawLeads.filter(l => {
                    const leadJobId = l.jobId || l.JobId || l.jobID; 
                    return String(leadJobId) === String(jobId);
                });

                // 4. Process Quotes (quotesRes.data will be [] if 404 occurred)
                const rawQuotes = quotesRes.data || [];
                
                setInviteCount(taskLeads.length);
                setQuoteCount(rawQuotes.length);

            } catch (err) {
                console.error("Failed to load widget stats", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [jobId]);

    if (!jobId) return null; 
    if (loading) return <span style={{fontSize:'0.75rem', color:'#adb5bd'}}>Loading...</span>;

    return (
        <div className="job-status-widget-compact" style={{gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
            {/* Invites Pill */}
            <div 
                onClick={(e) => { e.stopPropagation(); onShowDetails(task, 'invites'); }}
                style={{
                    display:'flex', alignItems:'center', gap:'4px', 
                    fontSize:'0.75rem', padding:'4px 8px', 
                    background:'#e7f0ff', color:'#0052cc', 
                    borderRadius:'20px', cursor:'pointer', fontWeight: 600,
                    border: '1px solid #b3d1ff', whiteSpace: 'nowrap'
                }}
                title="View Invited Professionals"
            >
                <UserPlus size={12} />
                {inviteCount} Invites
            </div>

            {/* Quotes Pill */}
            <div 
                onClick={(e) => { e.stopPropagation(); onShowDetails(task, 'quotes'); }}
                style={{
                    display:'flex', alignItems:'center', gap:'4px', 
                    fontSize:'0.75rem', padding:'4px 8px', 
                    background: quoteCount > 0 ? '#d1e7dd' : '#f8f9fa', 
                    color: quoteCount > 0 ? '#0f5132' : '#6c757d', 
                    borderRadius:'20px', cursor:'pointer', fontWeight: 600,
                    border: quoteCount > 0 ? '1px solid #badbcc' : '1px solid #dee2e6', whiteSpace: 'nowrap'
                }}
                title="View Received Quotes"
            >
                <MessageSquare size={12} />
                {quoteCount} Quotes
            </div>
            
            <button 
                className="icon-btn-circle" 
                style={{width: '28px', height: '28px'}}
                title="Manage Job" 
                onClick={(e) => { e.stopPropagation(); window.open(`/jobs/${jobId}`, '_blank'); }}
            >
                <Settings size={14} />
            </button>
        </div>
    );
};

const JobDetailsModal = ({ isOpen, onClose, jobId, jobTitle, initialTab = 'invites' }) => {
    const [activeTab, setActiveTab] = useState(initialTab);
    const [selectedBusinessId, setSelectedBusinessId] = useState(null);
    const [isBusinessProfileOpen, setIsBusinessProfileOpen] = useState(false);
    const [leads, setLeads] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);

    // Quote Modal State
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [isQuoteDetailModalOpen, setIsQuoteDetailModalOpen] = useState(false);
    const [isAssignConfirmModalOpen, setIsAssignConfirmModalOpen] = useState(false);
    const [quoteToAssign, setQuoteToAssign] = useState(null);
    const [isAssigning, setIsAssigning] = useState(false);

    useEffect(() => {
        if (isOpen && jobId) {
            setActiveTab(initialTab);
            fetchData();
        }
    }, [isOpen, jobId]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-AU', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            });
        } catch (e) {
            return 'Invalid Date';
        }
    };

    const openQuoteDetailModal = (quote) => {
        setSelectedQuote(quote);
        setIsQuoteDetailModalOpen(true);
    };

    const closeQuoteDetailModal = () => {
        setSelectedQuote(null);
        setIsQuoteDetailModalOpen(false);
    };

    const handleAssignJob = (quote) => {
        setQuoteToAssign(quote);
        setIsAssignConfirmModalOpen(true);
    };

    const confirmAssignJob = async () => {
        if (!quoteToAssign || !quoteToAssign.business?.businessId) {
            showToast('error', 'Business details are missing for this quote.');
            return;
        }
        setIsAssigning(true);
        try {
            await axios.post(`${API_BASE_URL}/api/Jobs/${jobId}/hire/${quoteToAssign.business.businessId}`);
            showToast('success', `Job assigned to ${quoteToAssign.business.businessName}!`);
            setIsAssignConfirmModalOpen(false);
            setQuoteToAssign(null);
            closeQuoteDetailModal(); // Close quote modal too if assigned? Maybe keep it open or close it. ViewJobQuotes closes confirmation only.
            fetchData(); // Refresh data
        } catch (err) {
            console.error("Error assigning job:", err);
            showToast('error', `Failed to assign job: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsAssigning(false);
        }
    };

    const handleMessageClickConcise = async (quote) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            
            const { nameid: currentUserId } = jwtDecode(token);
            const myuserType = localStorage.getItem('userType'); // 'Home' or 'Business'
            
            const isHome = myuserType === 'Home' || myuserType === 'HomeUser';

            // IMPORTANT: Ensure quote has businessuserid. The quote object from API should have it.
            // If it's undefined, this will fail.
            const senderBusinessUserId = isHome ? quote.businessuserid : currentUserId;
            const homeUserId = isHome ? currentUserId : undefined;
            const receiverBusinessUserId = !isHome ? quote.businessuserid : undefined;

            let url = "";
            if (isHome) {
                url = `senderBusinessUserId=${senderBusinessUserId}&homeUserId=${homeUserId}`;
            } else {
                url = `senderBusinessUserId=${senderBusinessUserId}&receiverBusinessUserId=${receiverBusinessUserId}`;                
            }

            const { data } = await api.get(`/api/Chat/NewConversation?${url}`);
            const { conversationId } = data;
            const path = isHome ? `/home_messages?conversationId=${conversationId}` : `/business_messages?conversationId=${conversationId}`;
            
            window.open(path, '_blank', 'noopener,noreferrer');
        } catch (err) {
            console.error('Error starting conversation:', err);
            showToast('error', 'Failed to start conversation');
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const { userId, apiUserType } = getLocalStorageUser();

            if(!userId) {
                console.warn("JobDetailsModal: No userId found in storage.");
                return;
            }

            // 1. Fetch Leads
            const leadsRes = await axios.get(`${API_BASE_URL}/api/Jobs/getjobleads?userId=${userId}&userType=${apiUserType}`);
            console.log('leadsRes');
            console.log(leadsRes);
            // 2. Fetch Quotes (Safe handling for 404)
            let quotesData = [];
            try {
                const quotesRes = await axios.get(`${API_BASE_URL}/api/Jobs/quote/${jobId}`);
                quotesData = quotesRes.data;
            } catch (err) {
                if (err.response && err.response.status === 404) {
                    quotesData = []; // No quotes found is OK
                } else {
                    console.error("Error loading quotes:", err);
                }
            }

            // 3. Process Leads
            const rawLeads = Array.isArray(leadsRes.data) ? leadsRes.data : (leadsRes.data?.result || []);
            const filteredLeads = rawLeads.filter(l => {
                const leadJobId = l.jobId || l.JobId || l.jobID;
                return String(leadJobId) === String(jobId);
            });

            // 4. Enrich Leads with Business Profile Data
            const enrichedLeads = await Promise.all(filteredLeads.map(async (lead) => {
                if (lead.businessId) {
                    try {
                        const businessData = await getBusinessProfileDataWithBID(lead.businessId);
                        return { ...lead, businessDetails: businessData };
                    } catch (error) {
                        console.error(`Failed to fetch business profile for businessId: ${lead.businessId}`, error);
                        return lead;
                    }
                }
                return lead;
            }));

            setLeads(enrichedLeads);
            setQuotes(quotesData || []);

        } catch (error) {
            console.error("Error fetching job details", error);
        } finally {
            setLoading(false);
        }
    };

    const renderStars = (rating) => {
        if (!rating) return null;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <Star size={12} fill="#ffc107" color="#ffc107" />
                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{rating.toFixed(1)}</span>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content-large" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>&times;</button>
                
                <h2 style={{textAlign:'left', marginBottom:'1rem'}}>{jobTitle}</h2>
                
                <div style={{display:'flex', gap:'1rem', borderBottom:'1px solid #e9ecef', marginBottom:'1.5rem'}}>
                    <button 
                        style={{
                            padding:'10px 20px', 
                            background:'none', 
                            border:'none', 
                            borderBottom: activeTab === 'invites' ? '2px solid #0066ff' : '2px solid transparent',
                            fontWeight: activeTab === 'invites' ? 600 : 400,
                            cursor:'pointer',
                            color: activeTab === 'invites' ? '#0066ff' : '#6c757d'
                        }}
                        onClick={() => setActiveTab('invites')}
                    >
                        Invites ({leads.length})
                    </button>
                    <button 
                        style={{
                            padding:'10px 20px', 
                            background:'none', 
                            border:'none', 
                            borderBottom: activeTab === 'quotes' ? '2px solid #28a745' : '2px solid transparent',
                            fontWeight: activeTab === 'quotes' ? 600 : 400,
                            cursor:'pointer',
                            color: activeTab === 'quotes' ? '#28a745' : '#6c757d'
                        }}
                        onClick={() => setActiveTab('quotes')}
                    >
                        Quotes ({quotes.length})
                    </button>
                </div>

                <div className="modal-body" style={{minHeight: '200px'}}>
                    {loading ? <div style={{textAlign:'center', padding:'2rem'}}>Loading...</div> : (
                        <>
                            {activeTab === 'invites' && (
                                <div className="leads-list">
                                    {leads.length === 0 ? <p>No professionals invited yet.</p> : (
                                        leads.map((lead, idx) => {
                                            const biz = lead.businessDetails;
                                            let profilePic = "";
                                            if (biz?.model.profilePic) {
                                                if (biz.model.profilePic.startsWith('data:image')) {
                                                    profilePic = biz.model.profilePic;
                                                } else {
                                                    profilePic = `${API_BASE_URL}${biz.model.profilePic}`;
                                                }
                                            }
                                            
                                            const businessName = biz?.model.businessName || "Contractor";
                                            const rating = biz?.model.ratingoverall || biz?.googlerating;
                                            const reviewCount = biz?.model.reviewcount || biz?.googleratingcount || 0;

                                            return (
                                                <div key={idx} style={{padding:'1rem', border:'1px solid #e9ecef', borderRadius:'8px', marginBottom:'10px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                                    <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
                                                        {/* Masked Profile Pic */}
                                                        <div style={{
                                                            width: '50px', height: '50px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                                                            border: '2px solid #e9ecef'
                                                        }}>
                                                            <img src={profilePic} alt={businessName} style={{width:'100%', height:'100%', objectFit:'cover'}} 
                                                                 onError={(e) => {e.target.onerror = null; e.target.src=""}}
                                                            />
                                                        </div>

                                                        <div>
                                                            <h4 style={{margin:0, fontSize:'1rem'}}>{businessName}</h4>
                                                            <div style={{display:'flex', alignItems:'center', gap:'8px', marginTop:'4px'}}>
                                                                {renderStars(rating)}
                                                                {reviewCount > 0 && <span style={{fontSize:'0.75rem', color:'#6c757d'}}>({reviewCount} reviews)</span>}
                                                            </div>
                                                            <span style={{fontSize:'0.8rem', color:'#6c757d', marginTop:'2px', display:'block'}}>
                                                                {lead.status || lead.Status || "Invited"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <button className="nav-button secondary" onClick={() => { setSelectedBusinessId(lead.businessId); setIsBusinessProfileOpen(true); }}>Profile</button>
                                                        <button className="nav-button secondary">Message</button>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
            <BusinessProfilePureCSS
                isOpen={isBusinessProfileOpen}
                onClose={() => setIsBusinessProfileOpen(false)}
                businessId={selectedBusinessId}
                useIDTYPE={"BID"}
                showMessageButton={false}
            />

            {/* Quote Detail Modal */}
            {isQuoteDetailModalOpen && selectedQuote && (
                <div className="quote-modal-overlay" onClick={closeQuoteDetailModal}>
                    <div className="quote-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button onClick={closeQuoteDetailModal} className="quote-modal-close-btn"><X size={20} /></button>
                        <h2>Quote from: {selectedQuote.business?.businessName || selectedQuote.Business?.businessName}</h2>
                        
                        <div className="quote-detail-item">
                            <DollarSignIcon size={18} /> 
                            <span>Price: ${Number(selectedQuote.price).toFixed(2)}</span>
                        </div>
                        <div className="quote-detail-item">
                            <CalendarDays size={18} /> 
                            <span>Quoted: {formatDate(selectedQuote.created_at)}</span>
                        </div>
                        
                        {(selectedQuote.business?.overallRating != null || selectedQuote.Business?.overallRating != null) && (
                            <div className="quote-detail-item">
                                <Star size={18} /> 
                                <span>Rating: {Number(selectedQuote.business?.overallRating || selectedQuote.Business?.overallRating).toFixed(1)}/5</span>
                            </div>
                        )}

                        <div className="quote-detail-section-title">Message:</div>
                        <p className="quote-message-box">{selectedQuote.message || 'No message provided.'}</p>
                        
                        <div className="quote-modal-actions">
                            <button 
                                onClick={() => handleMessageClickConcise(selectedQuote)} 
                                className="quote-action-button quote-btn-message"
                            >
                                <MessageSquare size={18} /> Message
                            </button>
                            <button 
                                onClick={() => handleAssignJob(selectedQuote)} 
                                className="quote-action-button quote-btn-assign"
                                disabled={false} // You might want to pass job status to disable this if already assigned
                            >
                                <CheckCircle size={18} /> Assign Job
                            </button>
                        </div>
                        
                        {/* View Profile Link */}
                        {(selectedQuote.business?.businessId || selectedQuote.Business?.businessId) && (
                            <button 
                                onClick={() => {
                                    setSelectedBusinessId(selectedQuote.business?.businessId || selectedQuote.Business?.businessId);
                                    setIsBusinessProfileOpen(true);
                                }} 
                                className="quote-profile-link"
                            >
                                <Info size={16}/> View {selectedQuote.business?.businessName || selectedQuote.Business?.businessName}'s Profile
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Assign Confirmation Modal */}
            {isAssignConfirmModalOpen && quoteToAssign && (
                <div className="quote-modal-overlay" onClick={() => setIsAssignConfirmModalOpen(false)}>
                    <div className="quote-modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '400px'}}>
                        <button onClick={() => setIsAssignConfirmModalOpen(false)} className="quote-modal-close-btn"><X size={20} /></button>
                        <h3>Confirm Assignment</h3>
                        <p>
                            Are you sure you want to assign this job to <strong>{quoteToAssign.business?.businessName || quoteToAssign.Business?.businessName}</strong> for 
                            <strong> ${Number(quoteToAssign.price).toFixed(2)}</strong>?
                        </p>
                        <div className="quote-modal-actions">
                            <button onClick={() => setIsAssignConfirmModalOpen(false)} className="quote-action-button quote-btn-cancel">
                                Cancel
                            </button>
                            <button onClick={confirmAssignJob} className="quote-action-button quote-btn-confirm" disabled={isAssigning}>
                                {isAssigning ? 'Assigning...' : 'Confirm & Assign'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

                            {activeTab === 'quotes' && (
                                <div className="quotes-list">
                                    {quotes.length === 0 ? <p>No quotes received yet.</p> : (
                                        quotes.map((quote, idx) => (
                                            <div key={idx} style={{padding:'1rem', border:'1px solid #28a745', background:'#f0fff4', borderRadius:'8px', marginBottom:'10px'}}>
                                                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'0.5rem'}}>
                                                    <h4 style={{margin:0}}>{quote.Business?.businessName || "Contractor"}</h4>
                                                    <strong style={{fontSize:'1.1rem', color:'#28a745'}}>${quote.price}</strong>
                                                </div>
                                                <p style={{margin:0, fontSize:'0.9rem', color:'#495057'}}>{quote.message}</p>
                                                <div style={{marginTop:'10px', textAlign:'right'}}>
                                                    <button className="nav-button primary" onClick={() => openQuoteDetailModal(quote)}>View Quote</button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};


// ==================================================================================
// --- MAIN COMPONENTS ---
// ==================================================================================

// --- HORIZONTAL TASK COMPONENT ---
const HorizontalTask = ({ task, onEdit, onDelete, onFindContractors, onShowDetails }) => {
    
    let statusIcon = <AlertCircle size={16} color="#ffc107" />;
    let statusText = "Pending";
    let statusClass = "status-pending";

    if (task.status === 'Active') {
        statusIcon = <Clock size={16} color="#007bff" />;
        statusText = "Active";
        statusClass = "status-active";
    } else if (task.status === 'Completed') {
        statusIcon = <CheckCircle size={16} color="#28a745" />;
        statusText = "Completed";
        statusClass = "status-completed";
    }

    return (
        <div className="task-card-vertical" data-id={task.id}>
            <div className="task-top-row">
                <GripVertical className="drag-handle" size={20} />
                <div className="task-info">
                    <h4>{task.name}</h4>
                    <p>{task.description}</p>
                </div>
                <div className="task-actions">
                    <button onClick={() => onEdit(task)} className="icon-button"><Edit size={14} /></button>
                    <button onClick={() => onDelete(task.id, task.phaseId)} className="icon-button delete-button"><Trash2 size={14} /></button>
                </div>
            </div>
            
            <div className="task-meta">
                <span className="trade-tag">{task.profession?.name || task.suggestedTrade || "General"}</span>
                <span className={`status-badge ${statusClass}`}>
                    {statusIcon} {statusText}
                </span>
            </div>

            <div className="task-footer">
                {task.status === 'Pending' || !task.status ? (
                    <button className="find-contractors-btn-compact" onClick={() => onFindContractors(task)}>
                        <Search size={14} /> Find Contractors
                    </button>
                ) : (
                    // SHOW WIDGET IN HORIZONTAL VIEW
                    <div style={{width: '100%', marginTop: '5px'}}>
                        <JobStatusWidget task={task} onShowDetails={onShowDetails} />
                    </div>
                )}
            </div>
        </div>
    );
};


// --- VERTICAL TASK COMPONENT ---
const VerticalTask = ({ task, onEdit, onDelete, onFindContractors, onShowDetails }) => {
    return (
        <div className="task-card-wide" data-id={task.id}>
            <div className="task-info-wide">
                <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                    <GripVertical className="drag-handle-vertical" size={18} style={{cursor:'grab', color:'#ccc'}} />
                    <div style={{flex:1}}>
                        <h4>
                            {task.name} 
                            <Edit size={12} className="mini-action-btn" onClick={() => onEdit(task)}/>
                            <Trash2 size={12} className="mini-action-btn" onClick={() => onDelete(task.id, task.phaseId)} style={{color:'red'}}/>
                        </h4>
                        <p>{task.profession?.name || task.suggestedTrade || "General"} • {task.description}</p>
                    </div>
                </div>
            </div>

            <div className="task-action-area-vertical">
                {task.status === 'Pending' || !task.status ? (
                    <button className="find-contractors-btn-compact" style={{width: '100%', display: 'flex', justifyContent: 'center', gap: '8px'}} onClick={() => onFindContractors(task)}>
                        <Search size={14} /> Find Contractors
                    </button>
                ) : task.status === 'Active' ? (
                    // SHOW WIDGET IN VERTICAL VIEW
                    <JobStatusWidget task={task} onShowDetails={onShowDetails} />
                ) : (
                     <div className="job-status-widget-compact">
                        <div className="job-status-text">
                            <CheckCircle size={16} color="green"/>
                            <span>Status: <strong>Completed</strong></span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- HORIZONTAL PHASE COMPONENT ---
const HorizontalPhase = ({ phase, setTasksForPhase, onAddTask, onEditPhase, onDeletePhase, onEditTask, onDeleteTask, onFindContractors, onShowDetails }) => {
    // Safety check: ensure tasks is always an array
    const tasks = Array.isArray(phase.tasks) ? phase.tasks : [];

    return (
        <div className="phase-column" data-id={phase.id}>
            <div className="phase-header">
                 <div className="phase-title-group" style={{display:'flex', alignItems:'center', flexGrow:1, color: 'black'}}>
                     <GripVertical className="drag-handle" size={20} />
                     <h3>{phase.name}</h3>
                </div>
                <div className="phase-actions">
                    <button onClick={() => onEditPhase(phase)} className="icon-button"><Edit size={16} /></button>
                    <button onClick={() => onDeletePhase(phase.id)} className="icon-button delete-button"><Trash2 size={16} /></button>
                </div>
            </div>

            <ReactSortable
                list={tasks}
                setList={(newTasks) => setTasksForPhase(phase.id, newTasks)}
                group="shared-tasks"
                animation={150}
                ghostClass="sortable-ghost"
                chosenClass="sortable-chosen"
                dragClass="sortable-drag"
                handle=".drag-handle"
                className="task-list"
                delay={10} 
            >
                {tasks.map((task) => (
                    <HorizontalTask
                        key={task.id}
                        task={task}
                        onEdit={onEditTask}
                        onDelete={onDeleteTask}
                        onFindContractors={onFindContractors}
                        onShowDetails={onShowDetails}
                    />
                ))}
            </ReactSortable>

            <button className="add-task-btn" onClick={() => onAddTask(phase.id)}>
                <Plus size={16} /> Add Task
            </button>
        </div>
    );
};

const ProjectDetail = () => {
    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [username, setUsername] = useState('');
    const [viewMode, setViewMode] = useState('horizontal');

    // Modals State
    const [isPhaseModalOpen, setIsPhaseModalOpen] = useState(false);
    const [currentPhase, setCurrentPhase] = useState(null);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [currentTask, setCurrentTask] = useState(null);
    const [currentPhaseIdForTask, setCurrentPhaseIdForTask] = useState(null);
    
    const [isFindContractorsModalOpen, setIsFindContractorsModalOpen] = useState(false);
    const [isBrowseProsModalOpen, setIsBrowseProsModalOpen] = useState(false);
    const [activeTaskForContractors, setActiveTaskForContractors] = useState(null);

    const [isJobDetailsModalOpen, setIsJobDetailsModalOpen] = useState(false);
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [selectedJobTitle, setSelectedJobTitle] = useState("");
    const [detailsModalTab, setDetailsModalTab] = useState('invites');

    const projectRef = useRef(project);
    useEffect(() => { projectRef.current = project; }, [project]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUsername(decoded.UserName || 'User');
            } catch(e) {
                console.error("Token decode error in main component", e);
            }
        }
    }, []);


    const openJobDetails = (task, initialTab) => {
        if(task && task.jobId) { 
            setSelectedJobId(task.jobId);
            setSelectedJobTitle(task.name);
            setDetailsModalTab(initialTab);
            setIsJobDetailsModalOpen(true);
        } else {
            showToast('error', 'Job ID not found for this task.');
        }
    };

    const fetchProject = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/UserProjects/${projectId}`);
            const projectData = response.data;
            
            // Normalize data structure
            if (projectData.phases) {
                projectData.phases = projectData.phases.map(p => ({
                    ...p,
                    id: p.phaseId || p.id,
                    tasks: (p.tasks || []).map(t => ({
                        ...t,
                        id: t.taskId || t.id,
                        phaseId: p.phaseId || p.id
                    })).sort((a, b) => a.order - b.order)
                })).sort((a, b) => a.order - b.order);
            } else {
                projectData.phases = [];
            }
            setProject(projectData);
        } catch (err) {
            setError('Failed to fetch project data.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (projectId) fetchProject();
    }, [projectId]);

    const handleSetPhases = (newPhases) => {
        setProject(prev => ({ ...prev, phases: newPhases }));
    };

    const handleSetTasksForPhase = (phaseId, newTasks) => {
        setProject(prev => {
            const newPhases = prev.phases.map(p => {
                if (p.id === phaseId) {
                    const updatedTasks = newTasks.map(t => ({...t, phaseId: phaseId}));
                    return { ...p, tasks: updatedTasks };
                }
                return p;
            });
            return { ...prev, phases: newPhases };
        });
    };

    const onEndPhaseDrag = async (evt) => {
        setTimeout(async () => {
            const currentProject = projectRef.current;
            if (!currentProject) return;
            try {
                const phaseIds = currentProject.phases.map(p => p.id);
                await axios.post(`${API_BASE_URL}/api/UserProjects/${projectId}/phases/reorder`, phaseIds);
            } catch (err) { console.error(err); }
        }, 100);
    };

    // --- CRUD Handlers ---

    const openAddPhaseModal = () => { setCurrentPhase(null); setIsPhaseModalOpen(true); };
    const openEditPhaseModal = (phase) => { setCurrentPhase(phase); setIsPhaseModalOpen(true); };
    const closePhaseModal = () => { setIsPhaseModalOpen(false); setCurrentPhase(null); };

    const handleSavePhase = async (phaseData) => {
        setIsLoading(true);
        try {
            const pId = parseInt(projectId);
            const userType = project.userType || "HomeUser"; 
            const ownerId = project.ownerUserId || 0;
            const payload = {
                PhaseId: phaseData.id || 0,
                ProjectId: pId,
                Name: phaseData.name,
                Description: phaseData.description || "", 
                Order: phaseData.order || 0,
                UserType: userType, 
                OwnerUserId: ownerId
            };

            if (phaseData.id) await axios.put(`${API_BASE_URL}/api/UserProjects/phases/${phaseData.id}`, payload);
            else await axios.post(`${API_BASE_URL}/api/UserProjects/${pId}/phases`, payload);
            
            showToast('success', phaseData.id ? 'Stage updated!' : 'Stage added!');
            closePhaseModal();
            fetchProject(); 
        } catch (err) { showToast('error', 'Failed to save stage.'); }
        finally { setIsLoading(false); }
    };

    const handleDeletePhase = async (phaseId) => {
        if (!window.confirm('Delete this stage and all tasks?')) return;
        setIsLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/UserProjects/phases/${phaseId}`);
            showToast('success', 'Stage deleted');
            fetchProject();
        } catch (err) { showToast('error', 'Failed to delete stage'); }
        finally { setIsLoading(false); }
    };

    const openAddTaskModal = (phaseId) => { setCurrentTask(null); setCurrentPhaseIdForTask(phaseId); setIsTaskModalOpen(true); };
    const openEditTaskModal = (task) => { setCurrentTask(task); setCurrentPhaseIdForTask(task.phaseId); setIsTaskModalOpen(true); };
    const closeTaskModal = () => { setIsTaskModalOpen(false); setCurrentTask(null); setCurrentPhaseIdForTask(null); };

    const handleSaveTask = async (taskData) => {
        setIsLoading(true);
        try {
            const payload = {
                TaskId: taskData.id || 0,
                PhaseId: taskData.phaseId,
                Name: taskData.name,
                Description: taskData.description || "",
                Status: taskData.status || "Pending",
                SuggestedTrade: taskData.suggestedTrade || "General",
                Order: taskData.order || 0,
                UserType: project.userType || "HomeUser",
                OwnerUserId: project.ownerUserId || 0
            };

            if (taskData.id) await axios.put(`${API_BASE_URL}/api/UserProjects/tasks/${taskData.id}`, payload);
            else await axios.post(`${API_BASE_URL}/api/UserProjects/phases/${taskData.phaseId}/tasks`, payload);
            
            showToast('success', taskData.id ? 'Task updated!' : 'Task added!');
            closeTaskModal();
            fetchProject();
        } catch (err) { showToast('error', 'Failed to save task'); }
        finally { setIsLoading(false); }
    };

    const handleDeleteTask = async (taskId, phaseId) => {
        if (!window.confirm('Delete this task?')) return;
        setIsLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/UserProjects/tasks/${taskId}`);
            showToast('success', 'Task deleted');
            fetchProject();
        } catch (err) { showToast('error', 'Failed to delete task'); }
        finally { setIsLoading(false); }
    };

    const openFindContractorsModal = (task) => { setActiveTaskForContractors(task); setIsFindContractorsModalOpen(true); };

    if (isLoading && !project) return <Loader2 />;
    if (error) return <div className="error-message">{error}</div>;
    if (!project) return <div className="error-message">Project not found.</div>;

    return (
        <>
            <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'projects'}/>
            
            <div className="pageContainer">
                <div className="project-manager">
                    {/* Header */}
                    <div className="project-header">
                        <div className="header-title-group">
                            <Link to="/projects" className="back-link"><ArrowLeft size={20} /></Link>
                            <div className="project-title-block">
                                <h2>{project.name}</h2>
                                <p>{project.description}</p>
                            </div>
                        </div>
                        <div className="project-actions">
                            <button onClick={() => setViewMode(v => v === 'horizontal' ? 'vertical' : 'horizontal')} className="switch-view-btn">
                                {viewMode === 'horizontal' ? <Rows size={18}/> : <LayoutTemplate size={18}/>}
                                {viewMode === 'horizontal' ? 'Vertical View' : 'Horizontal View'}
                            </button>
                        </div>
                    </div>

                    {/* --- VIEW MODES --- */}
                    
                    {viewMode === 'horizontal' ? (
                        /* ================== HORIZONTAL VIEW ================== */
                        <div className="horizontal-layout">
                            <ReactSortable
                                list={project.phases}
                                setList={handleSetPhases}
                                onEnd={onEndPhaseDrag}
                                group="phases"
                                animation={150}
                                ghostClass="sortable-ghost"
                                chosenClass="sortable-chosen"
                                handle=".drag-handle"
                                style={{ display: 'flex', gap: '1.5rem' }} 
                            >
                                {project.phases.map((phase) => (
                                    <HorizontalPhase
                                        key={phase.id}
                                        phase={phase}
                                        setTasksForPhase={handleSetTasksForPhase} 
                                        onAddTask={openAddTaskModal}
                                        onEditPhase={openEditPhaseModal}
                                        onDeletePhase={handleDeletePhase}
                                        onEditTask={openEditTaskModal}
                                        onDeleteTask={handleDeleteTask}
                                        onFindContractors={openFindContractorsModal}
                                        onShowDetails={openJobDetails} // Passed to HorizontalPhase
                                    />
                                ))}
                            </ReactSortable>
                            
                            <div className="add-phase-column">
                                <button id="add-phase-btn-column" onClick={openAddPhaseModal}>
                                    <Plus size={20} /> Add New Stage
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* ================== VERTICAL VIEW (New Layout) ================== */
                        <div className="plan-layout">
                            {/* LHS Image Column */}
                            <div className="layout-lhs">
                                <h2>Your Project Plan</h2>
                                <div style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0', color: '#0066ff' }}>
                                    <ClipboardList size={120} strokeWidth={1} />
                                </div>
                                <p style={{ color: '#6c757d' }}>
                                    This plan outlines every step of your renovation. <br/>
                                    Find contractors or invite pros for each task.
                                </p>
                            </div>

                            {/* RHS Accordion Column */}
                            <div className="layout-rhs">
                                <div className="phase-accordion">
                                    {project.phases.map((phase) => (
                                        <details key={phase.id} className="phase-card-accordion" open>
                                            <summary>
                                                <ChevronRight className="phase-chevron" />
                                                <div className="phase-info-accordion">
                                                    <h3>{phase.name}</h3>
                                                    <p>{phase.description}</p>
                                                </div>
                                                {/* Phase Actions */}
                                                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                                                     <button onClick={(e) => { e.preventDefault(); openAddTaskModal(phase.id); }} className="icon-button"><Plus size={18} /></button>
                                                     <button onClick={(e) => { e.preventDefault(); openEditPhaseModal(phase); }} className="icon-button"><Edit size={16} /></button>
                                                     <button onClick={(e) => { e.preventDefault(); handleDeletePhase(phase.id); }} className="icon-button delete-button"><Trash2 size={16} /></button>
                                                </div>
                                            </summary>
                                            
                                            <div className="phase-tasks-container">
                                                {(phase.tasks || []).length === 0 && (
                                                    <div style={{color:'#adb5bd', fontSize:'0.9rem', fontStyle:'italic', padding:'10px'}}>
                                                        No tasks yet.
                                                    </div>
                                                )}
                                                
                                                <ReactSortable
                                                    list={phase.tasks || []}
                                                    setList={(newTasks) => handleSetTasksForPhase(phase.id, newTasks)}
                                                    group="vertical-tasks"
                                                    animation={150}
                                                    ghostClass="sortable-ghost"
                                                    handle=".drag-handle-vertical"
                                                    className="task-list-vertical"
                                                    style={{ minHeight: '10px' }} 
                                                >
                                                    {(phase.tasks || []).map((task) => (
                                                        <VerticalTask
                                                            key={task.id}
                                                            task={task}
                                                            onEdit={openEditTaskModal}
                                                            onDelete={handleDeleteTask}
                                                            onFindContractors={openFindContractorsModal}
                                                            onShowDetails={openJobDetails}
                                                        />
                                                    ))}
                                                </ReactSortable>
                                            </div>
                                        </details>
                                    ))}
                                </div>

                                <button id="add-phase-btn-row" onClick={openAddPhaseModal}>
                                    <Plus size={20} /> Add New Phase
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modals */}
                {isPhaseModalOpen && <PhaseModal isOpen={isPhaseModalOpen} onClose={closePhaseModal} onSave={handleSavePhase} phase={currentPhase} />}
                {isTaskModalOpen && <TaskModal isOpen={isTaskModalOpen} onClose={closeTaskModal} onSave={handleSaveTask} task={currentTask} phaseId={currentPhaseIdForTask} />}
                
                <FindContractorsModal isOpen={isFindContractorsModalOpen} onClose={() => setIsFindContractorsModalOpen(false)} onPostJob={() => { setIsFindContractorsModalOpen(false); showToast('info', 'Job Wizard...'); }} onBrowsePros={() => { setIsFindContractorsModalOpen(false); setIsBrowseProsModalOpen(true); }} taskName={activeTaskForContractors?.name} />
                
                {isBrowseProsModalOpen && activeTaskForContractors && <BrowseProsModal isOpen={isBrowseProsModalOpen} onClose={() => setIsBrowseProsModalOpen(false)} trade={activeTaskForContractors.profession?.name || activeTaskForContractors.suggestedTrade} professionId={activeTaskForContractors.professionId} postcode={project.postcode} taskId={activeTaskForContractors.id} onInvite={() => fetchProject()} />}
            
                <JobDetailsModal 
                    isOpen={isJobDetailsModalOpen} 
                    onClose={() => setIsJobDetailsModalOpen(false)} 
                    jobId={selectedJobId} 
                    jobTitle={selectedJobTitle}
                    initialTab={detailsModalTab}
                />
            
            </div>
        </>
    );
};

export default ProjectDetail;
